import { useKftLocalStorage } from '~/composables/useKftLocalStorage'

interface DateRequestPage {
  id: string // date request ID from API
  eventDescriptionId: string
  url: string
  timestamp: number
  dates: string[] // ISO date strings
  dateType?: 'PUBLIC' | 'PRIVATE'
  numberOfTickets?: number
  isPrivateEvent?: boolean
  // Event data for display
  title?: string
  image?: string
  location?: string // city name
  price?: number // price in cents
  priceCurrency?: string
}

const STORAGE_KEY = 'activeDateRequestPages'

export const useDateRequestTracking = () => {
  const { data: activeDateRequests } = useKftLocalStorage<DateRequestPage[]>(
    STORAGE_KEY,
    []
  )

  const addDateRequestPage = (
    dateRequestId: string,
    eventDescriptionId: string,
    url: string,
    dates: string[],
    eventData?: {
      title?: string
      image?: string
      location?: string
      price?: number
      priceCurrency?: string
    },
    options?: {
      dateType?: 'PUBLIC' | 'PRIVATE'
      numberOfTickets?: number
      isPrivateEvent?: boolean
    }
  ) => {
    const existingRequestIndex = activeDateRequests.value.findIndex(
      (request) => request.id === dateRequestId
    )

    const newDateRequest: DateRequestPage = {
      id: dateRequestId,
      eventDescriptionId,
      url,
      timestamp: Date.now(),
      dates,
      dateType: options?.dateType,
      numberOfTickets: options?.numberOfTickets,
      isPrivateEvent: options?.isPrivateEvent,
      ...eventData,
    }

    if (existingRequestIndex >= 0) {
      // update existing request
      activeDateRequests.value[existingRequestIndex] = newDateRequest
    } else {
      // add new request
      activeDateRequests.value.push(newDateRequest)
    }

    return activeDateRequests.value
  }

  const removeDateRequestPage = (dateRequestId: string) => {
    activeDateRequests.value = activeDateRequests.value.filter(
      (request) => request.id !== dateRequestId
    )
    return activeDateRequests.value
  }

  const clearAllDateRequests = () => {
    activeDateRequests.value = []
  }

  const getActiveDateRequests = (): DateRequestPage[] => {
    return activeDateRequests.value
  }

  const getDateRequestById = (
    dateRequestId: string
  ): DateRequestPage | undefined => {
    return activeDateRequests.value.find(
      (request) => request.id === dateRequestId
    )
  }

  const getDateRequestByEventDescriptionId = (
    eventDescriptionId: string
  ): DateRequestPage | undefined => {
    return activeDateRequests.value.find(
      (request) => request.eventDescriptionId === eventDescriptionId
    )
  }

  const isDateRequestActive = (dateRequestId: string): boolean => {
    return getDateRequestById(dateRequestId) !== undefined
  }

  // cleanup expired date requests (older than 30 days)
  const cleanupExpiredDateRequests = () => {
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
    activeDateRequests.value = activeDateRequests.value.filter(
      (request) => request.timestamp > thirtyDaysAgo
    )
    return activeDateRequests.value
  }

  return {
    activeDateRequests: computed(() => activeDateRequests.value),
    addDateRequestPage,
    removeDateRequestPage,
    clearAllDateRequests,
    getActiveDateRequests,
    getDateRequestById,
    getDateRequestByEventDescriptionId,
    isDateRequestActive,
    cleanupExpiredDateRequests,
  }
}
